import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownOption, TabsComponent } from "@cms/shared-library";
import { TranslateService } from '@ngx-translate/core';
import { CheckEligibility } from 'src/app/models/check-eligibility';
import { PageContentModel } from 'src/app/models/page-content.model';
import { TrainingEligibilityForm } from 'src/app/models/training-eligibility-form.model';
import { TPWorker } from 'src/app/models/worker.model';
import { PageContentService } from 'src/app/services/page-content/page-content.service';
import { TrainingEligibilityService } from 'src/app/services/training-eligibility/training-eligibility.service';
import { TrainingProgramService } from 'src/app/services/training-program/training-program.service';
import { WorkerService } from 'src/app/services/worker-service/worker.service';
import { learnerIdValidatorPattern, phoneValidatorPattern, postalCodeValidatorPattern } from 'src/app/utils/validators.util';
import { OnExecuteData, OnExecuteErrorData, ReCaptchaV3Service } from "ng-recaptcha-2";
import { Subscription } from "rxjs";

export function minDateValidator(minDate: string): ValidatorFn {
  return (control : AbstractControl) : ValidationErrors | null => {
      const value = control.value;
      if (!value) {
          return null;
      }
      if (new Date(value) < new Date(minDate)) {
        return { minDate: true }
      }
      return null;
  }
}

@Component({
    selector: 'app-training-eligibility',
    templateUrl: './training-eligibility.component.html',
    styleUrls: ['../../components/black-button/black-button.component.scss', './training-eligibility.component.scss'],
    providers: [PageContentService],
    standalone: false
})

export class TrainingEligibilityComponent implements OnInit {
  //captcha
  public recentToken = "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public recentError?: { error: any };
  private singleExecutionSubscription: Subscription;

  trainingEligibilitySideBarContents: PageContentModel[];
  public today: string = (new Date()).toISOString().split('T')[0];
  currentLang: string = 'en';
  identificationOption: string = 'id';
  contactOption: string = 'email';
  trainingProgramOptionValue: string = '';
  _checkEligibility?: CheckEligibility;
  trainingEligibilityForm: TrainingEligibilityForm;
  formStep1: FormGroup;
  formStep2: FormGroup;
  form1Submitted = false;
  showNotFoundBanner: boolean = false
  status: number = 1;
  @ViewChild(TabsComponent) tabsComponent: TabsComponent;

  isWorkerPortal: boolean = false;
  worker: TPWorker;

  identificationOptions: DropdownOption[] = [
    new DropdownOption('Learner ID', 'ID de l’apprenant', 'id'),
    new DropdownOption('Name', 'Nom', 'name')
  ]

  contactOptions: DropdownOption[] = [
    new DropdownOption('Email address', 'Adresse courriel', 'email'),
    new DropdownOption('Phone number', 'Numéro de téléphone', 'phone'),
    new DropdownOption('Postal code', 'Code postal', 'postal')
  ]

  trainingProgramOptions: DropdownOption[] = [];

  constructor(public translateService: TranslateService,
    public router: Router,
    public pageContentService: PageContentService,
    @Inject('TrainingProgramService') private trainingProgramService: TrainingProgramService,
    @Inject('TrainingEligibilityService') private trainingEligibilityService: TrainingEligibilityService,
    @Inject('WorkerService') public workerService: WorkerService,
    private formBuilder: FormBuilder, private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service) {
    this.currentLang = translateService.currentLang
    translateService.onLangChange.subscribe(lang => {
      this.currentLang = lang.lang;
    });
  }

  ngOnInit(): void {
    this.trainingProgramService.GetAllTrainingPrograms(this.currentLang == 'fr', true).subscribe(data => {
      this.trainingProgramOptions = this.trainingProgramOptions.concat(data.map(x => { return new DropdownOption(x.Name, x.NameFr, x.TrainingProgramId) }))
    });

    this.pageContentService.GetPageContentByPageName("TrainingProviderSidebar").subscribe(data => {
      this.trainingEligibilitySideBarContents = data
    });
    this.buildForm();
    
    this.isWorkerPortal = this.router.url.indexOf('safety-and-prevention/eligibility-checker') == -1;

    this.workerService.GetCurrentWorker().subscribe(data => {
      const workerId = data.WorkerDisplayId!;
      if (!(workerId == null || workerId == undefined)) {
        this.workerService.GetWorkerById(workerId).subscribe(data => {
          this.worker = data;
          this.buildWorkerForm();
        });
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.singleExecutionSubscription) {
      this.singleExecutionSubscription.unsubscribe();
    }
  }

  checkEligibility() {
    if (this.singleExecutionSubscription) {
      this.singleExecutionSubscription.unsubscribe();
    }
    
    this.singleExecutionSubscription = this.recaptchaV3Service.execute('MpSearch').subscribe(
      (token) => {
        this.recentToken = token;
        this.recentError = undefined;
        this.trainingEligibilityForm = new TrainingEligibilityForm(
          this.formStep1.controls['learnerID']?.value, 
          this.formStep1.controls['learnerName']?.value, 
          this.formStep1.controls['emailID']?.value, 
          this.formStep1.controls['phoneNo']?.value, 
          this.formStep1.controls['postalCode']?.value, 
          this.trainingProgramOptionValue, 
          this.formStep2.controls['eligibilityOnDate']?.value, token);

       if(this.trainingProgramOptionValue !== '' && this.formStep2.controls['eligibilityOnDate']?.valid){
          this.trainingEligibilityService.CheckEligibility(this.trainingEligibilityForm).subscribe(data => {
            return this._checkEligibility = data;
          });
        }
          
      },
      (error) => {
        this.recentToken = "";
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.recentError = { error };
        console.log(error);
      },
    );
  }

  buildForm() {
    this.formStep1 = this.formBuilder.group({
      learnerID: new FormControl("", [Validators.maxLength(9), Validators.pattern("^[a-zA-Z0-9]*$"), Validators.required]),
      learnerName: new FormControl('', []),
      emailID: new FormControl('', [Validators.email, Validators.required]),
      phoneNo: new FormControl('', []),
      postalCode: new FormControl('', [])
    });

    this.formStep2 = this.formBuilder.group({ eligibilityOnDate: new FormControl("", [Validators.required, minDateValidator(this.today)]) });

  }

  buildWorkerForm() {
    this.formStep1 = this.formBuilder.group({
      learnerID: new FormControl(this.worker.WorkerDisplayId, [Validators.maxLength(9), Validators.pattern("^[a-zA-Z0-9]*$"), Validators.required]),
      learnerName: new FormControl(this.worker.FirstName + ' ' + this.worker.LastName, []),
      emailID: new FormControl(this.worker.EmailAddress, [Validators.email, Validators.required]),
      phoneNo: new FormControl(this.worker.PhoneNumber, []),
      postalCode: new FormControl(this.worker.PostalCode, [])
    });

    this.formStep2 = this.formBuilder.group({ eligibilityOnDate: new FormControl("", [Validators.required, minDateValidator(this.today)]) });

  }

  setIdentificationOption(option: string) {
    let tmp;

    if (option === "id") {
      this.clearValidator(this.formStep1.controls['learnerName'])
      tmp = this.formStep1.value;
      this.formStep1.controls['learnerID'].addValidators([Validators.maxLength(9), Validators.pattern(learnerIdValidatorPattern), Validators.required]);

    }
    else {
      this.clearValidator(this.formStep1.controls['learnerID'])
      tmp = this.formStep1.value;
      this.formStep1.controls['learnerName'].addValidators([Validators.maxLength(256), Validators.required]);
    }

    this.formStep1.reset();
    this.formStep1.setValue(tmp);
  }

  setContactOption(option: string) {
    let tmp;

    if (option === "email") {
      this.clearValidator(this.formStep1.controls['phoneNo'])
      this.clearValidator(this.formStep1.controls['postalCode'])
      tmp = this.formStep1.value;

      this.formStep1.controls['emailID'].addValidators([Validators.email, Validators.required]);
    }
    else if (option === "phone") {

      this.clearValidator(this.formStep1.controls['emailID'])
      this.clearValidator(this.formStep1.controls['postalCode'])
      tmp = this.formStep1.value;

      this.formStep1.controls['phoneNo'].addValidators([Validators.required, Validators.pattern(phoneValidatorPattern)]);

    }
    else {

      this.clearValidator(this.formStep1.controls['emailID'])
      this.clearValidator(this.formStep1.controls['phoneNo'])
      tmp = this.formStep1.value;
      this.formStep1.controls['postalCode'].addValidators([Validators.required, Validators.pattern(postalCodeValidatorPattern)]);
    }

    this.formStep1.reset();
    this.formStep1.setValue(tmp);
  }


  getSelectedTrainingProgram() {
    if (this.trainingProgramOptionValue && this.trainingProgramOptions && this.trainingProgramOptions.length > 0) {
      let selectedTrainingProgram = this.trainingProgramOptions.filter(v => v.value === this.trainingProgramOptionValue)[0];
      return this.currentLang === 'en' ? selectedTrainingProgram.name : selectedTrainingProgram.nameFr;
    }
    else {
      return '';
    }
  }

  resetStep1() {
    if (this.isWorkerPortal) {
      this.buildWorkerForm();
    } else {
      this.formStep1.reset();
    }
  }

  resetStep2() {
    this.formStep2.reset();
    this.trainingProgramOptionValue = ''
  }

  SelectStep(stepNumber: number) {
    if (stepNumber == 1) {
      this.trainingEligibilityForm = new TrainingEligibilityForm(this.formStep1.controls['learnerID']?.value, this.formStep1.controls['learnerName']?.value, this.formStep1.controls['emailID']?.value, this.formStep1.controls['phoneNo']?.value, this.formStep1.controls['postalCode']?.value, '00000000-0000-0000-0000-000000000000', undefined);
      this.trainingEligibilityService.CheckPersonFound(this.trainingEligibilityForm).subscribe(personFound => {
        if (personFound) this.navigateToStep(stepNumber)
        else this.showNotFoundBanner = true
      })
    } else this.navigateToStep(stepNumber)
  }

  private clearValidator(fc: AbstractControl) {
    fc.clearValidators();
    if (!this.isWorkerPortal) fc.setValue('');
  }

  private navigateToStep(stepNumber: number) {

    this.tabsComponent.selectStep(stepNumber);
    this._checkEligibility = undefined;

    let elem = document.getElementById('train-elig-title') as HTMLElement;
    if (elem !== null)
      elem.scrollIntoView({ behavior: 'smooth' })
  }

  backToMarketplace() {
    this.isWorkerPortal? this.router.navigate(['worker/training-schedules/course-search']) :
    this.router.navigate(['../../safety-and-prevention/cpo-approved-courses'], { relativeTo: this.route });
  }

  backToHome(): string {
    return this.isWorkerPortal ? 
      (this.currentLang == 'en'?'/worker/certifications':'/fr/worker/certifications') :
      (this.currentLang == 'en'?'/safety-and-prevention/home':'/fr/safety-and-prevention/home')
      
  }
}
